import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { palette, surfacePlasmaHoverSx, typographyTokens } from '../theme/tokens';

export type UiTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent';

export const chartColors = {
  neutral: '#D7DEE8',
  info: '#53CCFF',
  success: '#56E6B2',
  warning: '#D8AE68',
  danger: '#EF9AA2',
  accent: '#D8B6FF',
  cyan: '#53CCFF',
  rose: '#CFA3D7',
} as const;

export const chartPalette = [
  chartColors.info,
  chartColors.success,
  chartColors.warning,
  chartColors.accent,
  chartColors.danger,
  chartColors.cyan,
  chartColors.rose,
  chartColors.neutral,
] as const;

export const moduleColor: Record<string, string> = {
  equipment: chartColors.neutral,
  tmc: chartColors.warning,
  kanban: chartColors.neutral,
  audit5s: chartColors.success,
  visits: chartColors.cyan,
  kpi: chartColors.neutral,
  admin: chartColors.danger,
};

export const equipmentStatusColor: Record<string, string> = {
  on_stock: chartColors.success,
  in_use: chartColors.info,
  repair_pending_shipment: chartColors.cyan,
  in_repair: chartColors.warning,
  shipped: chartColors.neutral,
  transferred_to_lo: chartColors.info,
  lost: chartColors.danger,
  defective: chartColors.rose,
};

export function tonePalette(theme: Theme, tone: UiTone = 'neutral') {
  const mode = theme.palette.mode;
  const base = {
    neutral: theme.palette.text.primary,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    danger: theme.palette.error.main,
    accent: theme.palette.text.secondary,
  }[tone];

  const bgOpacity = mode === 'dark' ? 0.08 : 0.07;
  const borderOpacity = mode === 'dark' ? 0.22 : 0.18;

  return {
    main: base,
    softBg: alpha(base, bgOpacity),
    border: alpha(base, borderOpacity),
    hoverBg: alpha(base, mode === 'dark' ? 0.2 : 0.1),
  };
}

export const pagePaddingX = { xs: 1.5, sm: 2, md: 3 };
export const sectionGap = { xs: 1.5, md: 2 };

export const panelRadius = 1;

export function panelSx(theme: Theme) {
  const dark = theme.palette.mode === 'dark';
  return {
    border: '1px solid',
    borderColor: dark ? palette.dark.border : palette.light.border,
    bgcolor: dark ? palette.dark.surfaceElevated : theme.palette.background.paper,
    boxShadow: dark
      ? '0 1px 0 rgba(255,255,255,.026), 0 8px 20px rgba(0,0,0,.14)'
      : '0 1px 2px rgba(15,23,42,.04)',
    transition: 'border-color .15s ease, background-color .15s ease',
    ...surfacePlasmaHoverSx(theme, { activeOpacity: dark ? 0.2 : 0.16 }),
    '&:hover': {
      borderColor: dark ? palette.dark.borderHover : palette.light.borderHover,
      bgcolor: dark ? palette.dark.surfaceRaised : alpha(theme.palette.text.primary, 0.018),
    },
  } as const;
}

export function lineHoverSx(theme: Theme) {
  const dark = theme.palette.mode === 'dark';
  return {
    transition: 'border-color .15s ease, background-color .15s ease',
    '&:hover': {
      borderColor: dark ? palette.dark.borderHover : palette.light.borderHover,
      bgcolor: dark ? alpha(palette.dark.surfaceRaised, 0.5) : alpha(theme.palette.text.primary, 0.028),
    },
  } as const;
}

export function tableSurfaceSx(theme: Theme) {
  const dark = theme.palette.mode === 'dark';
  return {
    '& .MuiDataGrid-root, & .MuiTable-root': {
      fontVariantNumeric: 'tabular-nums',
    },
    '& .MuiDataGrid-columnHeader, & .MuiTableCell-head': {
      bgcolor: dark ? alpha(palette.dark.surfaceRaised, 0.38) : alpha(theme.palette.text.primary, 0.035),
      color: 'text.secondary',
      fontSize: 11,
      fontWeight: typographyTokens.weights.semibold,
      textTransform: 'none',
      letterSpacing: 0,
      whiteSpace: 'nowrap',
    },
    '& .MuiDataGrid-cell, & .MuiTableCell-root': {
      borderColor: dark ? palette.dark.border : palette.light.border,
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      bgcolor: dark ? alpha(palette.dark.surfaceRaised, 0.56) : alpha(theme.palette.text.primary, 0.035),
    },
  } as const;
}

export const semanticStatusTone: Record<string, UiTone> = {
  active: 'success',
  ok: 'success',
  success: 'success',
  on_stock: 'success',
  done: 'success',
  resolved: 'success',
  in_use: 'info',
  info: 'info',
  shipped: 'info',
  transferred_to_lo: 'info',
  in_repair: 'warning',
  repair_pending_shipment: 'warning',
  attention: 'warning',
  warning: 'warning',
  overdue: 'danger',
  defective: 'danger',
  lost: 'danger',
  critical: 'danger',
  error: 'danger',
  blocked: 'danger',
  written_off: 'neutral',
  pending: 'neutral',
};
